export default class FindResaleList {
    constructor() {
        this.section = document.querySelector('.component-find-resale-list');
        if (!this.section) return;

        this.business = this.section.getAttribute('data-business') || '';

        this.form = this.section.querySelector('#findResaleListForm');
        this.stateSelect = this.section.querySelector('#findResaleListState');
        this.citySelect = this.section.querySelector('#findResaleListCity');
        this.queryInput = this.section.querySelector('#findResaleListQuery');
        this.sortSelect = this.section.querySelector('#findResaleListSort');
        this.sortNearestOption = this.section.querySelector('#findResaleListSortNearest');
        this.geolocationBtn = this.section.querySelector('#findResaleListGeolocation');

        this.countLabel = this.section.querySelector('#findResaleListCount');
        this.sortWrapper = this.section.querySelector('#findResaleListSortWrapper');
        this.formError = this.section.querySelector('#findResaleListFormError');
        this.subtitle = this.section.querySelector('#findResaleListSubtitle');
        this.grid = this.section.querySelector('#findResaleListGrid');
        this.paginationWrapper = this.section.querySelector('#findResaleListPaginationWrapper');
        this.paginationInfo = this.section.querySelector('#findResaleListPaginationInfo');
        this.pagination = this.section.querySelector('#findResaleListPagination');

        this.userCoords = null;
        this.currentPage = 1;

        this.bindEvents();
    }

    bindEvents() {
        this.stateSelect.addEventListener('change', () => this.onStateChange());

        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.fetchResales(1);
        });

        [this.stateSelect, this.citySelect, this.queryInput].forEach((field) => {
            field.addEventListener('input', () => this.formError.classList.add('hidden'));
            field.addEventListener('change', () => this.formError.classList.add('hidden'));
        });

        this.sortSelect.addEventListener('change', () => this.fetchResales(1));

        this.geolocationBtn.addEventListener('click', () => this.onUseMyLocation());
    }

    hasAnyFilter() {
        return Boolean(
            this.stateSelect.value
            || this.citySelect.value
            || this.queryInput.value.trim()
            || this.userCoords
        );
    }

    onStateChange() {
        const uf = this.stateSelect.value;

        this.citySelect.innerHTML = '';
        this.citySelect.disabled = true;

        if (!uf) {
            this.citySelect.appendChild(this.buildOption('', 'Selecione um estado primeiro'));
            return;
        }

        this.citySelect.appendChild(this.buildOption('', 'Carregando municípios...'));

        fetch(`/getCitiesByState?state=${encodeURIComponent(uf)}`)
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then((cities) => {
                this.citySelect.innerHTML = '';
                this.citySelect.appendChild(this.buildOption('', 'Todos os municípios'));
                cities.forEach((city) => {
                    this.citySelect.appendChild(this.buildOption(city.id, city.name));
                });
                this.citySelect.disabled = false;
            })
            .catch((error) => {
                this.citySelect.innerHTML = '';
                this.citySelect.appendChild(this.buildOption('', 'Não foi possível carregar os municípios'));
                console.error('Erro ao buscar municípios:', error);
            });
    }

    buildOption(value, label) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        return option;
    }

    onUseMyLocation() {
        if (!navigator.geolocation) return;

        this.geolocationBtn.disabled = true;

        navigator.geolocation.getCurrentPosition(
            (position) => {
                this.userCoords = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                };
                this.sortNearestOption.disabled = false;
                this.sortSelect.value = 'nearest';
                this.geolocationBtn.disabled = false;
                this.fetchResales(1);
            },
            (error) => {
                this.geolocationBtn.disabled = false;
                console.error('Erro ao obter localização:', error);
            }
        );
    }

    buildParams(page) {
        const params = new URLSearchParams();
        params.set('page', page);
        params.set('sort', this.sortSelect.value);

        if (this.business) params.set('business', this.business);
        if (this.stateSelect.value) params.set('state', this.stateSelect.value);
        if (this.citySelect.value) params.set('city_id', this.citySelect.value);
        if (this.queryInput.value.trim()) params.set('q', this.queryInput.value.trim());

        if (this.sortSelect.value === 'nearest' && this.userCoords) {
            params.set('lat', this.userCoords.lat);
            params.set('lng', this.userCoords.lng);
        }

        return params;
    }

    fetchResales(page) {
        if (!this.hasAnyFilter()) {
            this.formError.classList.remove('hidden');
            this.countLabel.classList.add('hidden');
            this.sortWrapper.classList.remove('active');
            this.grid.innerHTML = '';
            this.paginationWrapper.classList.remove('active');
            return;
        }

        this.currentPage = page;
        this.countLabel.classList.remove('hidden');
        this.countLabel.textContent = 'Buscando revendas...';
        this.sortWrapper.classList.add('active');
        this.grid.innerHTML = '';
        this.paginationWrapper.classList.remove('active');

        fetch(`/getResaleList?${this.buildParams(page).toString()}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then((result) => this.renderResult(result))
            .catch((error) => {
                this.countLabel.textContent = 'Não foi possível carregar as revendas.';
                console.error('Erro ao carregar revendas:', error);
            });
    }

    renderResult(result) {
        const data = result.data || [];
        const hasResults = result.total > 0;

        this.countLabel.classList.toggle('hidden', !hasResults);
        this.sortWrapper.classList.toggle('active', hasResults);

        if (hasResults) {
            this.countLabel.textContent = `${result.total} ${result.total === 1 ? 'revenda ou oficina encontrada' : 'revendas ou oficinas encontradas'}`;
        }

        this.renderSubtitle(data);
        this.renderCards(data);
        this.renderPagination(result);
    }

    renderSubtitle(data) {
        const state = this.stateSelect.value;
        const cityLabel = this.citySelect.options[this.citySelect.selectedIndex]?.text;
        const hasCityFilter = this.citySelect.value && cityLabel;

        if (hasCityFilter && state) {
            this.subtitle.textContent = `Resultados para: ${cityLabel}, ${state}`;
            this.subtitle.classList.remove('hidden');
        } else if (state) {
            this.subtitle.textContent = `Resultados para: ${state}`;
            this.subtitle.classList.remove('hidden');
        } else {
            this.subtitle.classList.add('hidden');
        }
    }

    renderCards(data) {
        if (data.length === 0) {
            this.grid.insertAdjacentHTML('beforeend', this.emptyStateTemplate());
            return;
        }

        data.forEach((resale) => this.grid.insertAdjacentHTML('beforeend', this.cardTemplate(resale)));
    }

    emptyStateTemplate() {
        return `
            <div class="col-span-full flex flex-col items-center gap-4 py-10 text-center">
                <div class="flex h-20 w-20 items-center justify-center rounded-full bg-amber-50 text-black">
                    <svg width="32" height="32" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 19L13 13M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </div>
                <p class="text-xl font-black leading-7 text-shellcinza dark:text-white">Não encontramos revendas ou oficinas</p>
                <p class="text-sm font-normal leading-tight text-neutral300 dark:text-white">
                    Não há resultados para a localização selecionada.<br>
                    Selecione outro município ou estado nos campos acima e clique em Buscar.
                </p>
            </div>
        `;
    }

    cardTemplate(resale) {
        const cityState = [resale.city?.name, resale.state].filter(Boolean).join(' - ');
        const directionsUrl = resale.lat && resale.lng
            ? `https://www.google.com/maps/dir/?api=1&destination=${resale.lat},${resale.lng}`
            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(resale.address || resale.title || '')}`;

        return `
            <div class="flex min-h-28 flex-row items-center justify-between gap-2.5 rounded-lg border border-neutral300/30 p-4">
                <div class="flex flex-row items-center gap-4">
                    <div class="shrink-0">
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M26 13.5C26 20.5 16 28 16 28C16 28 6 20.5 6 13.5C6 10.8478 7.05357 8.3043 8.92893 6.42893C10.8043 4.55357 13.3478 3.5 16 3.5C18.6522 3.5 21.1957 4.55357 23.0711 6.42893C24.9464 8.3043 26 10.8478 26 13.5Z" fill="#FBC600"/><path d="M16.0001 16.9C17.8779 16.9 19.4001 15.3777 19.4001 13.5C19.4001 11.6222 17.8779 10.1 16.0001 10.1C14.1223 10.1 12.6001 11.6222 12.6001 13.5C12.6001 15.3777 14.1223 16.9 16.0001 16.9Z" fill="white"/></svg>
                    </div>
                    <div class="flex flex-col gap-2">
                        ${resale.title ? `<p class="text-base font-medium leading-snug text-shellcinza dark:text-white">${resale.title}</p>` : ''}
                        ${resale.address ? `<p class="text-sm font-normal leading-tight text-shellcinza dark:text-white">${resale.address}${cityState ? ` &middot; ${cityState}` : ''}</p>` : ''}
                        ${resale.phone ? `<p class="text-sm font-normal leading-tight"><a href="tel:${resale.phone.replace(/[^\d+]/g, '')}" class="text-shellcinza hover:font-bold dark:text-white">${resale.phone}</a></p>` : ''}
                    </div>
                </div>
                <a href="${directionsUrl}" target="_blank" rel="noopener"
                class="flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-medium leading-tight text-shellcinza hover:font-bold dark:text-white">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 2L9.66671 14C9.63746 14.0638 9.5905 14.1179 9.53141 14.1558C9.47232 14.1938 9.40359 14.2139 9.33338 14.2139C9.26317 14.2139 9.19444 14.1938 9.13535 14.1558C9.07626 14.1179 9.0293 14.0638 9.00005 14L6.66671 9.33333L2.00005 7C1.93622 6.97075 1.88213 6.92379 1.84421 6.8647C1.80629 6.80561 1.78613 6.73688 1.78613 6.66667C1.78613 6.59646 1.80629 6.52772 1.84421 6.46863C1.88213 6.40954 1.93622 6.36258 2.00005 6.33333L14 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <span>Como chegar</span>
            </a>
            </div>
        `;
    }

    renderPagination(result) {
        const { current_page: current, last_page: last, from, to, total } = result;

        if (total === 0) {
            this.paginationWrapper.classList.remove('active');
            return;
        }

        this.paginationInfo.textContent = `Exibindo ${from}-${to} de ${total} resultados`;
        this.pagination.innerHTML = '';

        this.pagination.appendChild(
            this.pageButton('‹', current - 1, current === 1, 'Página anterior')
        );

        this.buildPageList(current, last).forEach((page) => {
            if (page === '...') {
                const span = document.createElement('span');
                span.textContent = '...';
                span.className = 'px-2 text-sm text-shellcinza dark:text-white';
                this.pagination.appendChild(span);
                return;
            }

            this.pagination.appendChild(this.pageButton(String(page), page, false, `Página ${page}`, page === current));
        });

        this.pagination.appendChild(
            this.pageButton('›', current + 1, current === last, 'Próxima página')
        );

        this.paginationWrapper.classList.add('active');
    }

    pageButton(label, page, disabled, ariaLabel, active = false) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.disabled = disabled;
        button.setAttribute('aria-label', ariaLabel);
        if (active) button.setAttribute('aria-current', 'page');

        button.className = [
            'flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium transition',
            active ? 'bg-shellyellow text-black' : 'bg-transparent text-shellcinza hover:bg-neutral300/10 dark:text-white',
            disabled ? 'cursor-not-allowed opacity-40 hover:bg-transparent' : '',
        ].join(' ');

        if (!disabled) {
            button.addEventListener('click', () => this.fetchResales(page));
        }

        return button;
    }

    buildPageList(current, last) {
        const delta = 1;
        const pages = [];

        for (let page = 1; page <= last; page += 1) {
            const withinDelta = page >= current - delta && page <= current + delta;
            if (page === 1 || page === last || withinDelta) {
                pages.push(page);
            }
        }

        return pages.reduce((list, page, index) => {
            const previous = pages[index - 1];
            if (previous && page - previous > 1) list.push('...');
            list.push(page);
            return list;
        }, []);
    }
}

if (document.querySelector('.component-find-resale-list')) {
    new FindResaleList();
}
